import { useState, useEffect, useCallback, useRef } from "react";
import { X } from "lucide-react";

interface StockPhotoPickerProps {
  open: boolean;
  onSelect: (imageUrl: string) => void;
  onClose: () => void;
}

interface PexelsPhoto {
  id: number;
  src: {
    medium: string;
    large: string;
    original: string;
  };
  photographer: string;
  alt: string;
}

export default function StockPhotoPicker({ open, onSelect, onClose }: StockPhotoPickerProps) {
  const [query, setQuery] = useState("");
  const [photos, setPhotos] = useState<PexelsPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchPhotos = useCallback(async (searchQuery: string) => {
    try {
      const endpoint = searchQuery.trim()
        ? `/api/pexels/search?query=${encodeURIComponent(searchQuery)}&per_page=30`
        : `/api/pexels/curated?per_page=30`;
      const res = await fetch(endpoint);
      const data = await res.json();
      setPhotos(data.photos || []);
    } catch {
      setPhotos([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setPhotos([]);
      setLoading(true);
      fetchPhotos("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, fetchPhotos]);

  useEffect(() => {
    if (!open) return;
    if (!query) {
      setLoading(true);
      fetchPhotos("");
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => fetchPhotos(query), 300);
    return () => clearTimeout(timer);
  }, [query, open, fetchPhotos]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[70vh] bg-(--scroll-bg) rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-(--scroll-border)">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search stock photos..."
            className="flex-1 bg-(--scroll-bg-secondary) text-(--scroll-text) rounded-lg px-3 py-2 text-sm outline-none placeholder:text-(--sl-text-tertiary)"
          />
          <button onClick={onClose} className="p-1 text-(--sl-text-secondary) hover:text-(--scroll-text)">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-1">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-(--sl-text-secondary) border-t-transparent rounded-full animate-spin" />
            </div>
          ) : photos.length === 0 ? (
            <p className="text-center text-(--sl-text-tertiary) py-8 text-sm">
              {query ? "No photos found" : "Loading..."}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-1">
              {photos.map((photo) => (
                <button
                  key={photo.id}
                  onClick={() => onSelect(photo.src.large)}
                  className="relative aspect-square rounded-lg overflow-hidden group"
                >
                  <img
                    src={photo.src.medium}
                    alt={photo.alt || "Stock photo"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-white text-[10px] truncate block">{photo.photographer}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="text-center text-[10px] text-(--sl-text-tertiary) py-2">
          Powered by Pexels
        </p>
      </div>
    </div>
  );
}
